import { useForm, UseFormReturn, useFieldArray } from "react-hook-form";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import useAccess from "@/hooks/useAccess";
import Pagination from "@/components/atoms/Pagination";
import Output from "@/types/Sow/Output";
import {
  ChevronDown,
  ChevronRight,
  CircleX,
  Plus,
  SquarePen,
  Eye,
} from "lucide-react";
import TabTable, { SetData, SetOpen } from "../../Account/Tabs/TabTable";
import Button from "@/components/atoms/Button";
import type FormFieldItem from "@/types/Form/FormFieldItem";

import Select from "@/components/molecules/Select";
import Input from "@/components/atoms/Input";
import { zodResolver } from "@hookform/resolvers/zod";
import OutputSchema from "@/schemas/output"; 
import { router } from "@inertiajs/react";
import TabSheet, { MODE } from "../../Account/Tabs/TabSheet";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@/components/molecules/Table";
import axios from "axios";
import Sow from "@/types/Sow";
import * as Constants from "@/constants";
import { DatePicker } from "@/components/atoms/DatePicker";
import SearchBox from "@/components/molecules/SearchBox";
import { capitalize } from "@/lib/utils";
import { addMonths, format, isAfter, startOfMonth, endOfMonth } from "date-fns";
import ServiceType from "@/types/ServiceType";
import InternalTicketType from "@/types/InternalTicketType";
import { toUtcDate } from "@/lib/utils";

type Props = {
  sow: Sow;
  redirect_props?: any;
  serviceTypes: ServiceType[];
  internalTicketTypes: InternalTicketType[];
};

type FormValues = {
  rate: number | null;
  currency: string;
  number_of_month: number | null;
  monthly_ticket_count: number | null;
  total_ticket_count: number | null;
  total_amount: number | null;
  start_date: Date | null;
  end_date: Date | null;
  service_type_id: string;
  internal_ticket_type_id: string;
  internal_sub_ticket_type_id: string;
  periods: any[];
};

const OutputTab = ({ sow, redirect_props, serviceTypes, internalTicketTypes }: Props) => {
  const outputs = sow?.outputs ?? [];
  const [pagination, setPagination] = useState({ page: 1, per_page: 10 });
  const [totalCount, setTotalCount] = useState<number>(outputs.length);
  const [totalPages, setTotalPages] = useState<number>(Math.ceil(totalCount / pagination.per_page));

  const { getAccess } = useAccess();
  const [edit, setEdit] = useState<boolean>(false);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string | null>(null);
  const [filteredOutputs, setFilteredOutputs] = useState<Output[]>(outputs);
  const [loading, setLoading] = useState(false);
  const [baseError, setBaseError] = useState<string | null>(null);
  const [unfreezeSow, setUnfreezeSow] = useState<boolean>(false);
  const [subTicketTypeOptions, setSubTicketTypeOptions] = useState<{ label: string; value: string }[]>([]);

  const handleUnfreezeSowChange = (value: boolean) => {
    setUnfreezeSow(value);
  };

  const sowStartDate = new Date(sow.start_date);
  sowStartDate.setHours(0, 0, 0, 0);

  const sowEndDate = new Date(sow.end_date);
  sowEndDate.setHours(0, 0, 0, 0);

  const handleSearch = (search: string) => {
    setSearch(search);
    const filteredData = outputs.filter((output) =>
      !search
        ? true
        : (output.service_type.name.toLowerCase().includes(search.toLowerCase()) ||
            output.internal_ticket_type.name.toLowerCase().includes(search.toLowerCase()) ||
            output.internal_sub_ticket_type.name.toLowerCase().includes(search.toLowerCase()))
    );
    setFilteredOutputs(filteredData)
    setTotalCount(filteredData.length);
    setTotalPages(Math.ceil(filteredData.length / pagination.per_page));
  };

  const defaultValues = {
    rate: null,
    currency: "",
    number_of_month: null,
    monthly_ticket_count: null,
    total_ticket_count: null,
    total_amount: null,
    start_date: null,
    end_date: null,
    service_type_id: "",
    internal_ticket_type_id: "",
    internal_sub_ticket_type_id: "",
    periods: [],
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(OutputSchema),
    defaultValues,
  });

  const { control, setValue, watch } = form;
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "periods",
  });

  const outputStartDate = (() => {
    if (watch("start_date")) {
      return watch("start_date");
    }
  
    if (createOpen) {
      if (sow.invoiced_periods && sow.invoiced_periods.length > 0) {
        const lastPeriod = sow.invoiced_periods[sow.invoiced_periods.length - 1];
        const year = lastPeriod.slice(0, 4);
        const month = lastPeriod.slice(4, 6);
        
        const nextMonth = month === "12" ? "01" : String(parseInt(month) + 1).padStart(2, "0");
        const nextYear = month === "12" ? String(parseInt(year) + 1) : year;
  
        return `${nextYear}-${nextMonth}-01`;
      } else {
        return sow.start_date;
      }
    }
  
    if (edit) {
      const periods = form.getValues("periods");
  
      if (periods && periods.length > 0) {
        const sortedPeriods = periods.map(period => period.maand).sort();
        const smallestPeriod = sortedPeriods[0];
        const year = smallestPeriod.slice(0, 4);
        const month = smallestPeriod.slice(4, 6);
        
        return `${year}-${month}-01`;
      } else if (sow.invoiced_periods && sow.invoiced_periods.length > 0) {
        const lastPeriod = sow.invoiced_periods[sow.invoiced_periods.length - 1];
        const year = lastPeriod.slice(0, 4);
        const month = lastPeriod.slice(4, 6);
        
        const nextMonth = month === "12" ? "01" : String(parseInt(month) + 1).padStart(2, "0");
        const nextYear = month === "12" ? String(parseInt(year) + 1) : year;
  
        return `${nextYear}-${nextMonth}-01`;
      }
    }
  
    return sow.start_date;
  })();

  const internalTicketType = watch("internal_ticket_type_id");
  const outputEndDate = watch("end_date") || sow.end_date;

  useEffect(() => {
    const selectedInternalTicketType = internalTicketTypes.find(
      (type) => type.id.toString() === internalTicketType
    );
    
    if (selectedInternalTicketType && selectedInternalTicketType.children) {
      setSubTicketTypeOptions(
        selectedInternalTicketType.children.map((child) => ({
          label: child.name,
          value: child.id.toString(),
        }))
      );
    } else {
      setSubTicketTypeOptions([]);
    }

    const currentStartDate = form.getValues("start_date");
    const currentEndDate = form.getValues("end_date");
  
    if (outputStartDate && currentStartDate !== outputStartDate) {
      form.setValue("start_date", toUtcDate(outputStartDate));
    }
    if (outputEndDate && currentEndDate !== outputEndDate) {
      form.setValue("end_date", toUtcDate(outputEndDate));
    }
  }, [internalTicketType]);

  useEffect(() => {
    const currentStartDate = form.getValues("start_date");
    const currentEndDate = form.getValues("end_date");
  
    if (outputStartDate && currentStartDate !== outputStartDate) {
      form.setValue("start_date", toUtcDate(outputStartDate));
    }
    if (outputEndDate && currentEndDate !== outputEndDate) {
      form.setValue("end_date", toUtcDate(outputEndDate));
    }
  }, [outputStartDate, outputEndDate]);

  

  useEffect(() => {
    if (!search) {
      setFilteredOutputs(outputs);
    } else {
   
      const filteredData = outputs.filter((output) =>
        (
          output.service_type.name.toLowerCase().includes(search.toLowerCase()) ||
          output.internal_ticket_type.name.toLowerCase().includes(search.toLowerCase()) ||
          output.internal_sub_ticket_type.name.toLowerCase().includes(search.toLowerCase())
        )
      );
      setFilteredOutputs(filteredData);
    }
  }, [outputs, search]);

  const updatePeriods = () => {
    if (outputStartDate && outputEndDate) {
      const start = startOfMonth(toUtcDate(outputStartDate));
      const end = endOfMonth(toUtcDate(outputEndDate));


      const existingPeriods = form.getValues("periods");
      const areAllPeriodsZero = existingPeriods.every(
        period => parseFloat(period.rate) === 0 && parseFloat(period.ticketsCount) === 0
      );

      let defaultMonthlyRate = "0";
      let defaultMonthlyTicketsCount = "0";
      
      if (areAllPeriodsZero) {
        defaultMonthlyRate = parseFloat(String(form.getValues("rate"))).toFixed(2) || "0";
        defaultMonthlyTicketsCount = parseFloat(String(form.getValues("monthly_ticket_count"))).toFixed(2) || "0";
      }

      const newPeriods = [];
      let current = start;

      while (!isAfter(current, end)) {
        const formattedDate = format(current, "yyyyMM");

        const existingPeriod = existingPeriods.find((p) => p.maand === formattedDate);
        if (existingPeriod) {
         
          newPeriods.push({
            maand: existingPeriod.maand,
            rate: existingPeriod.rate,
            ticketsCount: existingPeriod.ticketsCount,
          });
        } else {
          newPeriods.push({
            maand: formattedDate,
            rate: defaultMonthlyRate,
            ticketsCount: defaultMonthlyTicketsCount,
          });
        }

        current = addMonths(current, 1);
      }

      replace(newPeriods);
    }
  };

  const updateOutputFields = () => {
    const periods = watch("periods");

    const totalTicketCount = periods.reduce((acc, period) => acc + (parseFloat(period.ticketsCount) || 0), 0).toFixed(2);
    const totalAmount = periods.reduce(
      (acc, period) => acc + (parseFloat(period.ticketsCount) * parseFloat(period.rate) || 0),
      0
    ).toFixed(2);
    const numberOfMonths = periods.length;

    setValue("total_ticket_count", totalTicketCount, { shouldValidate: true });
    setValue("total_amount", totalAmount, { shouldValidate: true });
    setValue("number_of_month", numberOfMonths, { shouldValidate: true });
  };

  const columns = (
    setOpen: SetOpen,
    setData: SetData
  ): ColumnDef<Output>[] => [
    {
      accessorKey: "expender",
      header: () => null,
      cell: ({ row }) =>
        row.original.periods.length ? (
          <div className="cursor-pointer" onClick={() => row.toggleExpanded()}>
            {row.getCanExpand() &&
              (row.getIsExpanded() ? <ChevronDown /> : <ChevronRight />)}
          </div>
        ) : null,
      size: 10,
      minSize: 10,
    },
    {
      accessorKey: "id",
      header: "ID",
    },
    {
      accessorKey: "service_type.name",
      header: "Service Type",
    },
    {
      accessorKey: "internal_ticket_type.name",
      header: "Internal Ticket Type",
    },
    {
      accessorKey: "internal_sub_ticket_type.name",
      header: "Internal Sub Ticket Type",
    },
    {
      accessorKey: "total_ticket_count",
      header: "Total Ticket Count",
    },
    {
      accessorKey: "total_amount",
      header: "Total Amount",
    },
    {
      accessorKey: "start_date",
      header: "Start Date",
    },
    {
      accessorKey: "end_date",
      header: "End Date",
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: (row: any) => {
        const output = row.row.original;
    
        const canDelete = output.periods.every((period: any) => {
          const maand = period.maand;
          const invoicedMatch = (sow.invoiced_periods || []).includes(maand);
     
          const rateticketsCountProduct = parseFloat(period.rate) * parseFloat(period.ticketsCount);

          if (invoicedMatch && rateticketsCountProduct !== 0) {
            return false;
          }

          return true;
        });
    
        return (
          <div className="flex space-x-2">
            <Eye
              className="cursor-pointer"
              size={20}
              style={{ opacity: !getAccess(["update_output"]) ? 0.5 : 1, pointerEvents: !getAccess(["update_output"]) ? 'none' : 'auto' }}
              onClick={() => {
                setData(
                  { ...row.row.original,
                    service_type_id: `${output.service_type.id}`,
                    internal_ticket_type_id: `${output.internal_ticket_type.id}`,
                    internal_sub_ticket_type_id: `${output.internal_sub_ticket_type.id}`,
                    rate: output.rate,
                    total_ticket_count: output.agreed_hours,
                    total_amount: output.total_amount,
                    start_date: toUtcDate(output.start_date),
                    end_date: toUtcDate(output.end_date),
                  });
                form.reset({
                  service_type_id: output.service_type.id || "",
                  internal_ticket_type_id: output.internal_ticket_type.id || "",
                  internal_sub_ticket_type_id: output.internal_sub_ticket_type.id || "",
                  rate: output.rate || null,
                  number_of_month: output.number_of_month || null,
                  monthly_ticket_count: output.monthly_ticket_count || null,
                  total_ticket_count: output.total_ticket_count || null,
                  total_amount: output.total_amount || null,
                  start_date: output.start_date ? toUtcDate(output.start_date) : null,
                  end_date: output.end_date ? toUtcDate(output.end_date) : null,
                  periods: output.periods || [],
                });
                setOpen(true);
              }}
            />
            <CircleX
              className="cursor-pointer"
              size={20}
              style={{ opacity: (canDelete && getAccess(["delete_output"])) ? 1 : 0.5, pointerEvents: (getAccess(["delete_output"]) && canDelete) ? 'auto' : 'none' }}
              onClick={() => {
                if (getAccess(["delete_output"]) && canDelete) {
                  if (confirm(`Are you sure you want to delete the output "${output.id}"?`)) {
                    router.delete(`/outputs/${output.id}`, {
                      onStart: () => setLoading(true),
                      onFinish: () => setLoading(false),
                    });
                  }
                }
              }}
            />
          </div>
        );
      },
      size: 20,
    }
  ];

  const onSubmit = (mode: MODE, values: any, original: Output | null) => {
  
    const payload = {
      sow_id: sow.id,
      currency: sow.currency,
      service_type_id: values.service_type_id || "",
      internal_ticket_type_id: values.internal_ticket_type_id || "",
      internal_sub_ticket_type_id: values.internal_sub_ticket_type_id || "",
      rate: values.rate || null,
      number_of_month: values.number_of_month || null,
      monthly_ticket_count: values.monthly_ticket_count || null,
      total_ticket_count: values.total_ticket_count || null,
      total_amount: values.total_amount || null,
      start_date: formatToISO(values.start_date),
      end_date: formatToISO(values.end_date),
      periods: values.periods || [],
    };
  
    if (mode === "edit") {
      router.put(`/outputs/${original?.id}`, payload, {
        onStart: () => setLoading(true),
        onFinish: () => setLoading(false),
      });
    }
    else if (mode === "create") {
      router.post("/outputs", payload, {
        onStart: () => setLoading(true),
        onFinish: () => setLoading(false),
      });
    }
  };

  const formatToISO = (dateString: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
  
    const timezoneOffsetMs = date.getTimezoneOffset() * 60 * 1000;
    const adjustedDate = new Date(date.getTime() - timezoneOffsetMs);
  
    return adjustedDate.toISOString();
  };

  const formFields: FormFieldItem<Output>[] = [
    {
      label: "Service Type",
      key: "service_type_id",
      required: true,
      group: 1,
      render: (props) => (
        <div className="relative">
          <Select
            disabled={!edit && !createOpen}
            options={
              serviceTypes.map((option) => ({
                label: option.name,
                value: option.id.toString(),
              }))
            }
            value={
              props.value !== undefined ? `${props.value}` : form.getValues("service_type_id") || ""
            }
            onChange={props.onChange}
            className="w-96"
            enabledSearch={true}
          />
        </div>
      ),
    },
    {
      label: "Internal Ticket Type",
      key: "internal_ticket_type_id",
      required: true,
      group: 1,
      render: (props) => (
        <div className="relative">
          <Select
            disabled={!edit && !createOpen}
            options={
              internalTicketTypes.map((option) => ({
                label: option.name,
                value: option.id.toString(),
              }))
            }
            value={
              props.value !== undefined ? `${props.value}` : form.getValues("internal_ticket_type_id") || ""
            }
            onChange={props.onChange}
            className="w-96"
            enabledSearch={true}
          />
        </div>
      ),
    },
    {
      label: "Internal Sub Ticket Type",
      key: "internal_sub_ticket_type_id",
      required: true,
      group: 1,
      render: (props) => (
        <div className="relative">
          <Select
            disabled={!edit && !createOpen}
            options={subTicketTypeOptions}
            value={
              props.value !== undefined ? `${props.value}` : form.getValues("internal_sub_ticket_type_id") || ""
            }
            onChange={props.onChange}
            className="w-96"
            enabledSearch={true}
          />
        </div>
      ),
    },
    {
      label: "Rate",
      key: "rate",
      required: true,
      group: 2,
      render: (props) => (
        <Input
          disabled={!edit && !createOpen}
          type="number"
          step="0.01"
          min="0"
          className="w-32"
          placeholder="Rate"
          {...props}
          value={props.value || ""}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d*\.?\d{0,2}$/.test(value)) {
              props.onChange(e);

              const periods = form.getValues("periods").map((period, index) => {
                if (!(sow?.invoiced_periods || []).includes(period.maand)) {
                  return { ...period, rate: value };
                }
                return period;
              });
              
              replace(periods);
            }
          }}
        />
      ),
    },
    {
      label: "Number Of Month",
      key: "number_of_month",
      required: true,
      group: 2,
      render: (props) => (
        <Input
          disabled={true}
          type="number"
          max={12}
          min={1}
          className="w-32"
          placeholder="N O M"
          {...props}
          value={props.value || ""}
          onChange={(e) => {
            const value = e.target.value ? parseInt(e.target.value, 10) : 0;
            if (value <= 12) {
              props.onChange(e);
            }
          }}
        />
      ),
    },
    {
      label: "Monthly Ticket Count",
      key: "monthly_ticket_count",
      required: true,
      group: 2,
      render: (props) => (
        <Input
          disabled={!edit && !createOpen}
          type="number"
          step="0.01"
          min="0"
          className="w-32"
          placeholder="M H"
          {...props}
          value={props.value || ""}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d*\.?\d{0,2}$/.test(value)) {
              props.onChange(e);

              const periods = form.getValues("periods").map((period) => {
                if (!(sow?.invoiced_periods || []).includes(period.maand)) {
                  return { ...period, ticketsCount: value };
                }
                return period;
              });
    
              replace(periods); 
            }
          }}
        />
      ),
    },
    {
      label: "Total Ticket Count",
      key: "total_ticket_count",
      required: true,
      group: 2,
      render: (props) => (
        <Input
          disabled={true}
          type="number"
          step="0.01"
          min="0"
          className="w-32"
          placeholder="T H"
          {...props}
          value={props.value || ""}
          onChange={props.onChange}
        />
      ),
    },
    {
      label: "Total Amount",
      key: "total_amount",
      required: true,
      group: 2,
      render: (props) => (
        <Input
          disabled={true}
          type="number"
          min="0"
          step="0.01"
          className="w-32"
          placeholder="T A"
          {...props}
          value={props.value || ""}
          onChange={props.onChange}
        />
      ),
    },
    {
      label: "Start Date",
      key: "start_date",
      required: false,
      group: 3,
      render: (props) => (
        <DatePicker
          minDate={
            createOpen
              ? unfreezeSow
                ? sowStartDate
                : (() => {
                    if (sow?.invoiced_periods && sow.invoiced_periods.length > 0) {
                      const lastPeriod = sow.invoiced_periods[sow.invoiced_periods.length - 1];
                      const year = parseInt(lastPeriod.slice(0, 4));
                      const month = parseInt(lastPeriod.slice(4, 6));
                      
                      const nextMonth = month === 12 ? 1 : month + 1;
                      const nextYear = month === 12 ? year + 1 : year;
                     
                      return new Date(nextYear, nextMonth - 1, 1);
                    } else {
                      return sowStartDate;
                    }
                    
                  })()
              : sowStartDate
          }
          maxDate={
            sow?.invoiced_periods && sow.invoiced_periods.length > 0
              ? unfreezeSow
                ? sowEndDate
                : (() => {
                    if (createOpen) {
                      return sowEndDate;
                    } else {
                      const firstPeriod = sow.invoiced_periods[0];
                      const year = parseInt(firstPeriod.slice(0, 4));
                      const month = parseInt(firstPeriod.slice(4, 6));
          
                      return new Date(year, month, 0);
                    }
                  })()
              : sowEndDate
          }
          placeholder="Start Date"
          value={props.value ? toUtcDate(props.value) : (outputStartDate ? toUtcDate(outputStartDate) : undefined)}
          onChange={(value) => {
            if (value) {
              props.onChange(toUtcDate(value, true));
            }
          }}
          disabled={!edit && !createOpen}
        />
      ),
    },
    {
      label: "End Date",
      key: "end_date",
      required: false,
      group: 3,
      render: (props) => (
        <DatePicker 
          maxDate={sowEndDate}
          minDate={
            sow?.invoiced_periods && sow.invoiced_periods.length > 0
              ? unfreezeSow
                ? sowStartDate
                : (() => {
                    const lastPeriod = sow.invoiced_periods[sow.invoiced_periods.length - 1];
                    const year = parseInt(lastPeriod.slice(0, 4));
                    const month = parseInt(lastPeriod.slice(4, 6));
                    
                    const nextMonth = month === 12 ? 1 : month + 1;
                    const nextYear = month === 12 ? year + 1 : year;
        
                    return new Date(nextYear, nextMonth - 1, 1);
                  })()
              : sowStartDate
          }
          placeholder="End Date"
          value={props.value ? toUtcDate(props.value) : (outputEndDate ? toUtcDate(outputEndDate) : undefined)}
          onChange={(value) => {
            if (value) {
              props.onChange(toUtcDate(value, true));
            }
          }}
          disabled={!edit && !createOpen}
        />
      ),
    },
    {
      label: "",
      key: "periods",
      required: false,
      group: 4,
      render: () => (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2">
        {form.getValues("periods").map((period, index) => (
          <div key={index} className="border border-border p-2 rounded-lg shadow-sm w-56">
            <h4 className="text-lg font-semibold text-center w-1/7">{formatServicePeriod(period.maand)}</h4>

            <Input
              type="hidden"
              value={period.maand}
              {...form.register(`periods.${index}.maand`)}
              readOnly
              className="w-full"
            />
          
            <div className="flex items-center mt-2">
              <span className="mr-2 w-40">Rate: </span>
              <Input
                disabled={
                  (sow?.invoiced_periods || []).includes(period.maand) 
                  ? (edit ? !unfreezeSow : (createOpen ? !unfreezeSow : true))
                  : !edit && !createOpen
                }
                type="text"
                step="0.01"
                placeholder="Rate"
                value={period.rate}
                {...form.register(`periods.${index}.rate`, {
                  onChange: handlePeriodChange,
                })}
                onInput={(e) => {
                  const input = e.target as HTMLInputElement;
                  const value = input.value;
                  if (value.includes(".")) {
                    const [integerPart, decimalPart] = value.split(".");
                    if (decimalPart.length > 2) {
                      input.value = `${integerPart}.${decimalPart.slice(0, 2)}`;
                    }
                  }

                  if (value.includes(",")) {
                    const [integerPart, decimalPart] = value.split(",");
                    if (decimalPart.length > 2) {
                      input.value = `${integerPart}.${decimalPart.slice(0, 2)}`;
                    }
                  }
                }}
                className="w-full"
              />
            </div>

            <div className="flex items-center mt-4">
              <span className="mr-2 w-40">Ticket Count: </span>
              <Input
                disabled={
                  (sow?.invoiced_periods || []).includes(period.maand) 
                  ? (edit ? !unfreezeSow : (createOpen ? !unfreezeSow : true))
                  : !edit && !createOpen
                }
                type="number"
                max={160}
                value={period.ticketsCount}
                step="0.01"
                placeholder="Ticket Count"
                {...form.register(`periods.${index}.ticketsCount`, {
                  onChange: handlePeriodChange,
                })}
                onInput={(e) => {
                  const input = e.target as HTMLInputElement;
                  const value = input.value;
                  if (value.includes(".")) {
                    const [integerPart, decimalPart] = value.split(".");
                    if (decimalPart.length > 2) {
                      input.value = `${integerPart}.${decimalPart.slice(0, 2)}`;
                    }
                  }

                  if (value.includes(",")) {
                    const [integerPart, decimalPart] = value.split(",");
                    if (decimalPart.length > 2) {
                      input.value = `${integerPart}.${decimalPart.slice(0, 2)}`;
                    }
                  }
                }}
                className="w-full"
              />
          </div>
          </div>
        ))}
        </div>
      ),
    },
  ];


  const handlePeriodChange = () => {
    const periods = form.getValues("periods");

    const totalTicketCount = periods.reduce((acc, period) => acc + (parseFloat(period.ticketsCount) || 0), 0).toFixed(2);

    const totalAmount = periods.reduce(
      (acc, period) => acc + (parseFloat(period.ticketsCount) * parseFloat(period.rate) || 0),
      0
    ).toFixed(2);

    setValue("total_ticket_count", totalTicketCount, { shouldValidate: true });
    setValue("total_amount", totalAmount, { shouldValidate: true });
  };

  useEffect(() => {
    updatePeriods();
  }, [outputStartDate, outputEndDate]);

  useEffect(() => {
    updateOutputFields();
  }, [fields, watch("periods")]);

  useEffect(() => {
    if (createOpen) {
      form.reset(defaultValues);
      updatePeriods();
    }
  }, [createOpen]);

  useEffect(() => {
    if (redirect_props && redirect_props.errors) {
      const errors = redirect_props.errors;
      let baseErrorFound = false;
  
      Object.entries(errors).forEach(([key, messages]) => {
        if (Array.isArray(messages)) {
          if (key === "base") {
            setBaseError(messages.join("&& "));
            baseErrorFound = true;
          } else {
            form.setError(key as keyof typeof form.getValues, {
              type: "manual",
              message: `${capitalize(key)} ${messages[0]}`,
            });
          }
        }
      });
  
      // If there are no base errors, reset the baseError state
      if (!baseErrorFound) {
        setBaseError(null);
      }
    } else {
      setCreateOpen(false);
      setEdit(false);
    }
  }, [redirect_props]);

  const formatServicePeriod = (servicePeriod :string) => {
    const year = servicePeriod.slice(0, 4);
    const month = servicePeriod.slice(4, 6);
    const date = toUtcDate(`${year}-${month}-01`);

    return date.toLocaleString('en-US', { month: 'short', year: 'numeric' }).replace(' ', '/');
  };

  const renderSubComponent = (
    { row }: { row: Row<Output> }
  ) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-x-1 gap-y-2 justify-center">
      <div className="col-span-full flex justify-center">
        <div className="flex flex-wrap justify-center gap-2 max-w-[800px]">
          {row.original.periods.map((period, index) => {
            const servicePeriod = period.maand;

            return (
              <div
                key={`${row.id}-${servicePeriod}-${index}`}
                className="border border-border rounded-md p-2 text-sm w-[120px]"
              >
                <div className="text-center font-semibold border-b border-border mb-1 pb-0.5">
                  {formatServicePeriod(servicePeriod)}
                </div>

                <div className="flex justify-between mb-0.5">
                  <span>Rate:</span>
                  <span className="text-right">{period.rate}</span>
                </div>

                <div className="flex justify-between mb-0.5">
                  <span className="flex flex-col leading-tight">
                    <span>Ticket</span>
                    <span>Count:</span>
                  </span>
                  <span className="text-right self-center">{period.ticketsCount}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const handlePageChange = (page: number) => { 
    setPagination(prev => ({ ...prev, page }));
  };

  return (
    <div>
      <div className="flex items-center justify-between pt-6 pb-4 m-4">
        <SearchBox onSearch={handleSearch} />

        <Button className="float-right m-4" disabled={!getAccess(["create_output"])} onClick={() => setCreateOpen(true)}>
          <Plus size={16} className="mr-2" />
          Add New Output
        </Button>
      </div>
   
      <TabTable<Output>
        columns={columns}
        data={filteredOutputs}
        form={form}
        formClass="space-y-4"
        formFields={formFields}
        onSubmit={onSubmit}
        edit={edit}
        setEdit={setEdit}
        renderSubComponent={renderSubComponent}
        currentPage={pagination.page}
        pageSize={pagination.per_page}
        isDifferentTableColumns= {true}
        sheetSide="top"
        loading={loading}
        baseError={baseError}
        showSwitch={(sow?.invoiced_periods || []).length > 0}
        onUnfreezeSowChange={handleUnfreezeSowChange}
      />
      
      <Pagination
        currentPage={pagination.page}
        totalPages={totalPages}
        totalCount={totalCount}
        perPage={pagination.per_page}
        onPageChange={handlePageChange}
      />

      <TabSheet
        title="Create Output"
        form={form}
        mode="create"
        data={null}
        open={createOpen}
        formClass="space-y-4"
        formFields={formFields}
        onOpen={setCreateOpen}
        onSubmit={onSubmit}
        side="top"
        baseError={baseError}
        showSwitch={(sow?.invoiced_periods || []).length > 0}
        onUnfreezeSowChange={handleUnfreezeSowChange}
      />
    </div>
  );
};

export default OutputTab;
